import React, {ChangeEvent, FC, useEffect, useState} from "react";
import {
    alpha,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Typography
} from "@mui/material";
import {Check, CheckOutlined, ErrorOutline, Replay} from "@mui/icons-material";
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import TextField from "@mui/material/TextField";
import LoadingButton from "@mui/lab/LoadingButton";
import QrReader from "react-qr-reader";

import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import ClientService from "../../../api/clientService";
import {toastError} from "../../shared/toast/Toast";
import {getSafeBackendErrorMessage} from "../../../util/utils";

interface IProps {
    open: boolean;
    handleDialogClose: () => void;
}

type Status = "idle" | "loading" | "error" | "success";

const ValidateVoucherDialog: FC<IProps> = ({open, handleDialogClose}) => {
    const {t} = useTranslation();
    const theme = useTheme();

    const [status, setStatus] = useState<Status>("idle");
    const [message, setMessage] = useState("");
    const [code, setCode] = useState("");
    const [comment, setComment] = useState("");
    const [leaveCommentMessage, setLeaveCommentMessage] = useState("");
    const [relatedVouchers, setRelatedVouchers] = useState<string[]>([]);
    const [selectedVouchers, setSelectedVouchers] = useState<string[]>([]);

    const isManualDisabled = code.length === 0

    // Reset state on open
    useEffect(() => {
        if (open) resetState();
    }, [open]);

    const resetState = () => {
        setStatus("idle");
        setMessage("");
        setCode("");
        setComment("");
        setSelectedVouchers([]);
        setRelatedVouchers([]);
        setLeaveCommentMessage("");
    };

    const handleChangeCode = (e: ChangeEvent<HTMLInputElement>) =>
        setCode(e.target.value.toUpperCase());

    const handleChangeComment = (e: ChangeEvent<HTMLInputElement>) =>
        setComment(e.target.value);

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSelectedVouchers((prev) =>
            e.target.checked ? [...prev, value] : prev.filter((v) => v !== value)
        );
    };

    const handleValidate = async (code: string) => {
        if (!code || status === "loading") return;
        setStatus("loading");

        try {
            const res = await ClientService.fetchRelatedVouchers(code);
            const related = res.data ?? [];
            setSelectedVouchers([code])
            if (!related.length) {
                await handleRedeem([code]);
            } else {
                setRelatedVouchers([code, ...related.map((v: any) => v.code_hex)]);
                setStatus("idle");
            }
        } catch (err: any) {
            setStatus("error");
            setMessage(err.response?.data?.detail ?? getSafeBackendErrorMessage(err));
        }
    };

    const handleRedeem = async (codes: string[]) => {
        if (!codes.length) return;
        setStatus("loading");
        try {
            const res = await ClientService.redeemVoucher(codes);
            setMessage(res.data?.detail ?? t("validate_success"));
            setStatus("success");
        } catch (err: any) {
            setStatus("error");
            setMessage(err.response?.data?.detail ?? getSafeBackendErrorMessage(err));
        }
    };

    const handleComment = async () => {
        if (!comment.trim() || !selectedVouchers.length) return;

        try {
            const res = await ClientService.updateTicketComment(selectedVouchers, comment);
            setLeaveCommentMessage(res.data?.detail ?? t("comment_saved"));
            setSelectedVouchers([]);
            setComment("");
        } catch (err: any) {
            toastError(getSafeBackendErrorMessage(err.response?.data?.detail));
        }
    };

    const handleScan = async (result: string | null) => {
        if (result) await handleValidate(result);
    };

    // --------------------- RENDER SECTIONS ---------------------

    const renderIdleContent = () => {
        if (relatedVouchers.length) {
            return (
                <>
                    <Typography variant="subtitle2">{t("related_vouchers_message")}</Typography>
                    <Box mt={2}>
                        {relatedVouchers.map((c) => (
                            <FormControlLabel
                                key={c}
                                value={c}
                                control={
                                    <Checkbox
                                        checked={selectedVouchers.includes(c)}
                                        onChange={handleCheckboxChange}
                                        color="secondary"
                                    />
                                }
                                label={c}
                            />
                        ))}
                    </Box>
                    <Grid container justifyContent="flex-end" mt={2}>
                        <LoadingButton
                            variant="contained"
                            color="secondary"
                            onClick={() => handleRedeem(selectedVouchers)}
                            disabled={!selectedVouchers.length}
                        >
                            {t("validate")}
                        </LoadingButton>
                    </Grid>
                </>
            );
        }

        return (
            <Box>
                <QrReader
                    facingMode="environment"
                    delay={2000}
                    onScan={handleScan}
                    onError={(e) => console.error(e)}
                />
                <Grid container my="1.5em" alignItems="center">
                    <Divider
                        sx={{
                            flex: 1,
                            background: `linear-gradient(to right, ${alpha(
                                theme.palette.secondary.main,
                                0.7
                            )}, ${alpha(theme.palette.secondary.main, 0)})`,
                            height: 2,
                        }}
                    />
                    <Typography variant="body2" color="secondary" px="1em">
                        {t("or_enter_manually")}
                    </Typography>
                    <Divider
                        sx={{
                            flex: 1,
                            background: `linear-gradient(to left, ${alpha(
                                theme.palette.secondary.main,
                                0.7
                            )}, ${alpha(theme.palette.secondary.main, 0)})`,
                            height: 2,
                        }}
                    />
                </Grid>

                <Grid container gap="1em" flexWrap="nowrap" alignItems="flex-start">
                    <TextField
                        label={t("voucher_code")}
                        fullWidth
                        variant="outlined"
                        autoComplete="off"
                        color="secondary"
                        value={code}
                        onChange={handleChangeCode}
                    />
                    <IconButton
                        size="small"
                        sx={{
                            mt: 1.5,
                            color: theme.palette.quaternary.main,
                            backgroundColor: `${alpha(
                                isManualDisabled
                                    ? theme.palette.secondary.light
                                    : theme.palette.quaternary.main,
                                0.2
                            )} !important`,
                        }}
                        disabled={isManualDisabled}
                        onClick={() => handleValidate(code)}
                    >
                        <CheckOutlined/>
                    </IconButton>
                </Grid>
            </Box>
        );
    };

    const renderLoading = () => (
        <Box textAlign="center">
            <CircularProgress size={75} color="secondary"/>
        </Box>
    );

    const renderError = () => (
        <Box textAlign="center">
            <ErrorOutline color="secondary" sx={{fontSize: "6rem"}}/>
            <Typography variant="h5" color="secondary" fontWeight="bold">
                {message}
            </Typography>
            <Button
                startIcon={<Replay/>}
                color="secondary"
                variant="outlined"
                sx={{mt: 2}}
                onClick={resetState}
            >
                {t("validate_again")}
            </Button>
        </Box>
    );

    const renderSuccess = () => (
        <Box textAlign="center">
            <Check color="secondary" sx={{fontSize: "6rem"}}/>
            <Typography variant="h5" color="secondary" fontWeight="bold">
                {message}
            </Typography>

            <Box p={2} border={`1px solid ${theme.palette.secondary.main}`} mt={2}>
                {selectedVouchers.length ? (
                    <>
                        <Typography variant="body1" color="secondary" mb={2}>
                            {t("leave_a_comment")}
                        </Typography>
                        <Grid container gap="1em" flexWrap="nowrap" alignItems="flex-start">
                            <TextField
                                label={t("comment")}
                                fullWidth
                                variant="outlined"
                                autoComplete="off"
                                color="secondary"
                                value={comment}
                                onChange={handleChangeComment}
                            />
                            <IconButton
                                size="small"
                                sx={{
                                    mt: 1.5,
                                    color: theme.palette.quaternary.main,
                                    backgroundColor: `${alpha(
                                        !comment.length
                                            ? theme.palette.secondary.light
                                            : theme.palette.quaternary.main,
                                        0.2
                                    )} !important`,
                                }}
                                disabled={!comment.length}
                                onClick={handleComment}
                            >
                                <CheckOutlined/>
                            </IconButton>
                        </Grid>
                    </>
                ) : (
                    <>
                        <Typography variant="body2" color="secondary" fontWeight={600}>
                            {leaveCommentMessage}
                        </Typography>
                        <Typography variant="body2" color="secondary">
                            {t("leave_comment_success")}
                        </Typography>
                    </>
                )}
            </Box>

            <Button
                startIcon={<Replay/>}
                color="secondary"
                variant="outlined"
                sx={{mt: 2}}
                onClick={resetState}
            >
                {t("validate_again")}
            </Button>
        </Box>
    );

    const renderContent = () => {
        switch (status) {
            case "loading":
                return renderLoading();
            case "error":
                return renderError();
            case "success":
                return renderSuccess();
            default:
                return renderIdleContent();
        }
    };

    return (
        <CustomDialog
            open={open}
            title={t("validate_voucher")}
            maxWidth="xs"
            handleDialogClose={handleDialogClose}
            content={renderContent()}
        />
    );
};

export default ValidateVoucherDialog;
